import { type MeetingMinutes } from "../openai/schemas/meeting-minutes";

/**
 * Render meeting minutes to Markdown. v1 generates meeting minutes only —
 * the class/custom variants died with the AI-chat removal.
 */
export function renderNotesToMarkdown(notes: MeetingMinutes): string {
  return renderMeetingMinutes(notes);
}

function renderMeetingMinutes(notes: MeetingMinutes): string {
  let md = `# ${notes.title}\n\n`;
  md += `**Date:** ${notes.date}\n\n`;

  if (notes.attendees && notes.attendees.length > 0) {
    md += `**Attendees:** ${notes.attendees.join(", ")}\n\n`;
  }

  md += `---\n\n`;

  // Agenda Items
  if (notes.agenda_items.length > 0) {
    md += `## Agenda\n\n`;
    notes.agenda_items.forEach((item, i) => {
      md += `### ${i + 1}. ${item.topic}\n\n`;
      md += `${item.summary}\n\n`;

      if (item.decisions.length > 0) {
        md += `**Decisions:**\n`;
        item.decisions.forEach((decision) => {
          md += `- ${decision}\n`;
        });
        md += `\n`;
      }

      if (item.action_items.length > 0) {
        md += `**Action Items:**\n`;
        item.action_items.forEach((action) => {
          const priority = action.priority ? ` [${action.priority}]` : "";
          const dueDate = action.due_date ? `, Due: ${action.due_date}` : "";
          md += `- [ ] ${action.task} (@${action.owner}${dueDate})${priority}\n`;
        });
        md += `\n`;
      }
    });
  }

  // All Action Items
  if (notes.action_items.length > 0) {
    md += `## Action Items Summary\n\n`;
    notes.action_items.forEach((action) => {
      const priority = action.priority ? ` [${action.priority.toUpperCase()}]` : "";
      const dueDate = action.due_date ? `, Due: ${action.due_date}` : "";
      md += `- [ ] **${action.owner}**: ${action.task}${dueDate}${priority}\n`;
    });
    md += `\n`;
  }

  // Open Questions
  if (notes.open_questions.length > 0) {
    md += `## Open Questions\n\n`;
    notes.open_questions.forEach((q, i) => {
      // CommonMark: continuation content must be indented past the list
      // marker, whose width grows with the number ("10. " is 4 columns) —
      // a fixed indent would detach the context bullet from question 10 on.
      const marker = `${i + 1}. `;
      md += `${marker}${q.question}\n`;
      md += `${" ".repeat(marker.length)}- ${q.context}\n\n`;
    });
  }

  // Risks
  if (notes.risks && notes.risks.length > 0) {
    md += `## Risks & Concerns\n\n`;
    notes.risks.forEach((risk) => {
      md += `- **[${risk.severity.toUpperCase()}]** ${risk.risk}\n`;
      if (risk.mitigation) {
        md += `  - *Mitigation:* ${risk.mitigation}\n`;
      }
      md += `\n`;
    });
  }

  // Per-item evidence (citations) is a generation-time grounding constraint,
  // deliberately not rendered — see schemas/meeting-minutes.ts.

  return md;
}
